import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Car,
  Shield,
  Heart,
  MapPin,
  Clock,
  CheckCircle,
  ChevronRight,
  Sparkles,
  Building2,
  Users,
  Star
} from 'lucide-react';
import {
  heroData,
  benefits,
  howItWorksSteps,
  partnerLocations
} from '../data/mock';

const iconMap = {
  car: Car,
  shield: Shield,
  heart: Heart,
};

const HomePage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden jolly-hero-gradient">
        {/* Decorative Elements - Hidden on mobile */}
        <div className="hidden md:block absolute top-10 right-10 w-24 h-24 rounded-full bg-[hsl(45,98%,80%)] opacity-60 sun-pulse" />
        <div className="hidden md:block absolute top-20 right-20 w-12 h-12 rounded-full bg-[hsl(33,100%,70%)] opacity-40" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Text Content */}
            <div className="space-y-6 md:space-y-8 text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight">
                <span className="jolly-text-teal">Safer. Happier.</span>
                <br />
                <span className="jolly-text-orange">Stress-Free</span>
                <br />
                <span className="jolly-text-teal">Shopping for Families.</span>
              </h1>
              <p className="text-gray-600 text-base md:text-lg max-w-xl mx-auto lg:mx-0">
                {heroData.subheadline}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/parents">
                  <Button className="jolly-btn-orange text-base px-8 py-4 w-full sm:w-auto">
                    For Parents
                  </Button>
                </Link>
                <Link to="/partners">
                  <Button className="jolly-btn-outline text-base px-8 py-4 w-full sm:w-auto">
                    For Malls & Partners
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Hero Image */}
            <div className="relative hero-illustration">
              <div className="relative z-10">
                <div className="rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_f4ef9e04-9139-4323-b854-e5d21a16af08/artifacts/bvu1uas1_New%20Home%20page%20image.png"
                    alt="Happy family with Jolly Tots Ride - mother pushing child in car-shaped trolley"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Road decoration at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-6 bg-gray-700">
          <div className="h-full flex items-center justify-around">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="w-12 h-1 bg-[hsl(45,100%,70%)]" />
            ))}
          </div>
        </div>
      </section>

      {/* Partner Logos */}
      <section className="bg-white py-8 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            <p className="text-gray-500 text-sm font-medium">Trusted at:</p>
            {partnerLocations.map((partner) => (
              <div key={partner.name} className="partner-logo">
                <span className="text-lg font-bold jolly-text-blue">{partner.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Jolly Tots Rides Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="jolly-section-title">Why Jolly Tots Rides:</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Making every shopping trip an adventure for your little ones while giving you peace of mind.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {benefits.map((benefit, index) => {
              const Icon = iconMap[benefit.icon];
              return (
                <Card key={benefit.id} className="jolly-card card-enter" style={{ animationDelay: `${index * 100}ms` }}>
                  <CardContent className="p-6 md:p-8 text-center">
                    <div className="feature-icon mx-auto mb-4 bg-[hsl(45,100%,90%)]">
                      <Icon className="w-8 h-8 jolly-text-orange" />
                    </div>
                    <h3 className="text-xl font-bold jolly-text-teal mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24 jolly-gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="jolly-section-title">How It Works</h2>
            <p className="text-gray-600 text-lg">Easy as 1-2-3-4!</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {howItWorksSteps.map((step, index) => (
              <div key={step.id} className="relative">
                <Card className="jolly-card h-full">
                  <CardContent className="p-6 text-center">
                    <div className="step-badge mx-auto mb-4 bg-[hsl(33,100%,50%)] text-white">
                      {step.step}
                    </div>
                    <h3 className="text-lg font-bold jolly-text-teal mb-3">{step.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                  </CardContent>
                </Card>
                {/* Arrow connector (hidden on last item and mobile) */}
                {index < howItWorksSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-8 h-8 text-[hsl(33,100%,50%)]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted by Families Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="jolly-section-title">
                Trusted by Families.
                <br />
                <span className="jolly-text-orange">Designed for Malls.</span>
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Jolly Tots Rides partners with leading shopping centres to enhance the family shopping experience. Our service increases dwell time, improves customer satisfaction, and positions malls as family-friendly destinations.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">Increased foot traffic from families</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">Enhanced customer experience</span>
                </div>
              </div>
              <Link to="/partners">
                <Button className="jolly-btn-blue mt-4">
                  Partner with Us
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            {/* Mascot Illustration */}
            <div className="relative">
              <div className="bg-gradient-to-br from-[hsl(210,70%,92%)] to-[hsl(210,60%,85%)] rounded-3xl p-8 md:p-12">
                <div className="text-center">
                  {/* Mascot Bear Image */}
                  <div className="inline-block">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_f4ef9e04-9139-4323-b854-e5d21a16af08/artifacts/l5hnk185_Jolly%20Bear%201.png"
                      alt="Jolly Bear Mascot"
                      className="w-40 h-auto md:w-52 mx-auto"
                    />
                  </div>
                  <p className="mt-6 text-lg font-bold jolly-text-teal">Meet Jolly Bear!</p>
                  <p className="text-gray-600 text-sm">Your friendly Jolly Attendant</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[hsl(33,100%,50%)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Make Shopping Fun?
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Find a Jolly Tots Ride at your nearest participating mall and give your child the adventure they deserve.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/parents">
              <Button className="bg-white text-[hsl(33,100%,40%)] hover:bg-gray-100 font-bold rounded-full px-8 py-4 text-base shadow-lg hover:shadow-xl transition-all duration-200">
                <MapPin className="w-5 h-5 mr-2" />
                Find a Location
              </Button>
            </Link>
            <Link to="/contact">
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold rounded-full px-8 py-4 text-base transition-all duration-200">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
